import { useCallback, useEffect, useRef, useState } from 'react';
import type { LoadMonitoringEvents, MonitoringEvent, MonitoringEventsPage, MonitoringEventsQuery } from './monitoringEvents';

type Collection = 'events' | 'background';
type State = { key: string; data: MonitoringEventsPage | null; loading: boolean; paging: Collection | null; error: boolean };
function mergeById(previous: MonitoringEvent[], added: MonitoringEvent[]) {
  const entries = new Map(previous.map(event => [event.event_id, event]));
  for (const event of added) entries.set(event.event_id, event);
  return [...entries.values()];
}
function validate(page: MonitoringEventsPage, query: MonitoringEventsQuery) {
  const p = page?.pagination;
  if (page?.schema_version !== 1 || !Array.isArray(page.events) || !Array.isArray(page.background)
    || !p || p.offset !== query.offset || !Number.isInteger(p.total) || !Number.isInteger(p.background_total)
    || [p.next_offset, p.background_next_offset].some(n => n !== null && (!Number.isInteger(n) || n <= query.offset))
    || [...page.events, ...page.background].some(e => !e.event_id || !e.feedback_signal_id || !e.company_id
      || !Array.isArray(e.sources) || !Array.isArray(e.timeline) || !Array.isArray(e.legacy_signal_ids)
      || query.company_id && e.company_id !== query.company_id)) throw new Error('Invalid monitoring events response');
}

/** Independent cursors; a failed page never replaces previously read history. */
export function useMonitoringEvents(load: LoadMonitoringEvents, query: MonitoringEventsQuery, contextKey: string, enabled: boolean) {
  const loader = useRef(load); loader.current = load;
  const key = JSON.stringify([query, contextKey, enabled]);
  const keyRef = useRef(key); keyRef.current = key;
  const generation = useRef(0), pending = useRef(false);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<State>({ key, data: null, loading: enabled, paging: null, error: false });
  const current = state.key === key ? state : { key, data: null, loading: enabled, paging: null, error: false };
  const stateRef = useRef(current); stateRef.current = current;
  useEffect(() => {
    const id = ++generation.current;
    pending.current = false;
    setState({ key, data: null, loading: enabled, paging: null, error: false });
    if (!enabled) return;
    const scoped: MonitoringEventsQuery = JSON.parse(key)[0];
    const read = loader.current;
    void Promise.resolve().then(() => read(scoped)).then(data => {
      validate(data, scoped);
      if (id === generation.current && key === keyRef.current) setState({ key, data, loading: false, paging: null, error: false });
    }).catch(() => {
      if (id === generation.current && key === keyRef.current) setState({ key, data: null, loading: false, paging: null, error: true });
    });
    return () => { generation.current += 1; };
  }, [key, retry]);
  const loadMore = useCallback(async (collection: Collection) => {
    const before = stateRef.current;
    const cursor = collection === 'events' ? 'next_offset' : 'background_next_offset';
    const offset = before.data?.pagination[cursor];
    if (offset == null || before.loading || pending.current || before.key !== keyRef.current) return;
    const id = generation.current, requestKey = keyRef.current;
    const scoped: MonitoringEventsQuery = { ...JSON.parse(requestKey)[0], offset };
    pending.current = true;
    setState(s => ({ ...s, paging: collection, error: false }));
    try {
      const page = await loader.current(scoped); validate(page, scoped);
      if (id !== generation.current || requestKey !== keyRef.current) return;
      setState(s => s.data ? ({ ...s, paging: null, error: false, data: { ...s.data,
        [collection]: mergeById(s.data[collection], page[collection]),
        pagination: { ...s.data.pagination, [cursor]: page.pagination[cursor] },
      } }) : s);
    } catch {
      if (id === generation.current && requestKey === keyRef.current) setState(s => ({ ...s, paging: null, error: true }));
    } finally { if (id === generation.current) pending.current = false; }
  }, []);
  return { ...current, loadMore, retry: () => setRetry(n => n + 1) };
}
